import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import {
  PlusCircleIcon,
  UserIcon,
  MapPinIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useElements,
  useStripe,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";

import CustomInputField from "../../../customInputField";
import {
  useLazyGetPaymentMehodsQuery,
  useSavePaymentMethodsMutation,
} from "../../../../store/paymentCreation";
import IconButton from "../../../iconButton";

const NewPaymentForm = ({ show, setShowSlide }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentErrorMessage, setPaymentErrorMessage] = useState("");
  const [expiryDateErrorMessage, setExpiryDateErrorMessage] = useState("");
  const [cvvErrorMessage, setCvvErrorMessage] = useState("");
  const [paymentLoader, setPaymentLoader] = useState(false);

  const [trigger] = useLazyGetPaymentMehodsQuery();

  const [savePaymentMethod] = useSavePaymentMethodsMutation();

  const validationSchema = Yup.object({
    cardHolderName: Yup.string()
      .required("Name is Required")
      .matches(
        /^(?![0-9])[a-zA-Z0-9\s]*$/,
        "Invalid name format. Use letters and spaces only."
      ),
    billingZipCode: Yup.string().required("ZIP Code is Required"),
    // creditCardNumber: Yup.string()
    //   .required("Credit card number is Required")
    //   .matches(
    //     /^[0-9]{16}$/,
    //     "Invalid credit card number. Must be 16 digits and only contain numbers."
    //   ),
    // expirationDate: Yup.date().required("Expiry Date is Required"),
    // cvv: Yup.string()
    //   .required("CVV is Required")
    //   .matches(/^\d{3}$/, "Invalid CVV. Must be 3 digits."),
  });

  const formik = useFormik({
    initialValues: {
      cardHolderName: "",
      billingZipCode: "",
      creditCardNumber: "",
      expirationDate: "",
      cvv: "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (formik.isValid) {
        // navigate("/signup/step/8");
      } else {
        console.log("Some fields are empty or invalid.");
      }
    },
  });

  const handleStripePayment = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    try {
      setPaymentLoader(true);
      const response = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: formik.values.cardHolderName,
          address: {
            postal_code: formik.values.billingZipCode,
          },
        },
      });

      if (response) {
        const paymentMethodId = response?.paymentMethod?.id;
        const params = {
          id: paymentMethodId,
        };
        const { data } = await savePaymentMethod(params);
        if (data) {
          setPaymentLoader(false);
          toast.success(data.message);
          trigger();
          setShowSlide(false);
        } else {
          setPaymentLoader(false);
        }
      }
    } catch (error) {
      console.log(error, "error of stripe");
    }
  };

  const handleClose = () => {
    setShowSlide(!show);
  };

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full px-[15px] py-5 md:p-0">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-[478px]">
                  <div className="flex h-full flex-col bg-white py-5 md:py-[40px] px-4 md:px-8 shadow-xl rounded-md md:rounded-0">
                    <div className="">
                      <div className="">
                        <Dialog.Title className="text-xl md:text-2xl font-bold leading-[160%] text-[#04091E] border-b border-solid border-[#CCD0D4] pb-[30px] mb-[30px]">
                          Add a New Payment Method
                        </Dialog.Title>
                      </div>
                    </div>
                    <form
                      className="dark-field mt-5 pb-5 mb-5 border-b border-solid border-strokeColor"
                      onSubmit={formik.handleSubmit}
                    >
                      <CustomInputField
                        icon={UserIcon}
                        placeHolder="Your Name on Credit Card"
                        type="text"
                        name="cardHolderName"
                        value={formik.values.cardHolderName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMessage={
                          formik.touched.cardHolderName &&
                          formik.errors.cardHolderName
                        }
                        classes="flex items-center h-[48px] md:h-[70px] border border-solid border-strokeColor rounded-[10px] bg-fieldBgColor mb-5 overflow-hidden"
                        inputClasses="border-r-0 lg:text-lg text-sm font-medium text-customGray placeholder:text-[#869099] pl-[10px] h-[50px] md:h-[70px] pr-[20px] bg-transparent"
                      />
                      <CustomInputField
                        icon={MapPinIcon}
                        placeHolder="Billing ZIP Code"
                        type="text"
                        name="billingZipCode"
                        value={formik.values.billingZipCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMessage={
                          formik.touched.billingZipCode &&
                          formik.errors.billingZipCode
                        }
                        classes="flex items-center h-[48px] md:h-[70px] border border-solid border-strokeColor rounded-[10px] bg-fieldBgColor mb-5 overflow-hidden"
                        inputClasses="border-r-0 lg:text-lg text-sm font-medium text-customGray placeholder:text-[#869099] pl-[10px] h-[50px] md:h-[70px] pr-[20px] bg-transparent"
                      />
                      <div className="test-stripe border border-solid border-strokeColor rounded-[10px] h-[48px] md:h-[70px] bg-fieldBgColor flex items-center mb-5">
                        <span className="pl-[14px] md:pl-[20px] pr-[10px] border-solid border-strokeColor border-r bg-transparent">
                          <CreditCardIcon className="icon w-6 h-6" />
                        </span>
                        <CardNumberElement
                          options={{
                            style: {
                              base: {
                                color: "#1C1C1C",
                                padding: "15px",
                                fontWeight: "400",
                                fontSize: "18px",
                                backgroundColor: "#F1F4F6",
                                "::placeholder": {
                                  color: "#869099",
                                },
                                invalid: {
                                  color: "#ff6666",
                                },
                              },
                            },
                          }}
                          onChange={(event) => {
                            formik.handleChange({
                              target: {
                                name: "creditCardNumber",
                                value: event.complete ? event.value : "",
                              },
                            });
                            setPaymentErrorMessage(event.error?.message ?? "");
                          }}
                        />
                      </div>
                      {paymentErrorMessage && (
                        <p className="text-red-500 text-xs leading-[160%] font-bold mb-4 -mt-4 block">
                          {paymentErrorMessage}
                        </p>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-[17px]">
                        <div>
                          <div className="test-stripe-exp border border-solid border-strokeColor rounded-[10px] h-[48px] md:h-[70px] bg-fieldBgColor flex items-center mb-5">
                            <span className="pl-[14px] md:pl-[20px] pr-[10px] border-solid border-strokeColor border-r bg-transparent">
                              <CalendarDaysIcon className="icon w-6 h-6" />
                            </span>
                            <CardExpiryElement
                              options={{
                                style: {
                                  base: {
                                    color: "#1C1C1C",
                                    padding: "15px",
                                    fontWeight: "400",
                                    fontSize: "18px",
                                    backgroundColor: "#F1F4F6",
                                    "::placeholder": {
                                      color: "#869099",
                                    },
                                    invalid: {
                                      color: "#ff6666",
                                    },
                                  },
                                },
                              }}
                              onChange={(event) => {
                                formik.handleChange({
                                  target: {
                                    name: "expirationDate",
                                    value: event.complete ? event.value : "",
                                  },
                                });
                                setExpiryDateErrorMessage(
                                  event.error?.message ?? ""
                                );
                              }}
                            />
                          </div>
                          {expiryDateErrorMessage && (
                            <p className="text-xs font-bold text-red-500 mb-4 -mt-4">
                              {expiryDateErrorMessage}
                            </p>
                          )}
                        </div>
                        <div>
                          <div className="test-stripe-cvc border border-solid border-strokeColor rounded-[10px] h-[48px] md:h-[70px] bg-fieldBgColor flex items-center">
                            <span className="pl-[14px] md:pl-[20px] pr-[10px] border-solid border-strokeColor border-r bg-transparent">
                              <LockClosedIcon className="icon w-6 h-6" />
                            </span>
                            <CardCvcElement
                              options={{
                                style: {
                                  base: {
                                    color: "#1C1C1C",
                                    padding: "15px",
                                    fontWeight: "400",
                                    fontSize: "18px",
                                    backgroundColor: "#F1F4F6",
                                    "::placeholder": {
                                      color: "#869099",
                                    },
                                    invalid: {
                                      color: "#ff6666",
                                    },
                                  },
                                },
                              }}
                              onChange={(event) => {
                                setCvvErrorMessage(event.error?.message ?? "");
                              }}
                            />
                          </div>
                          {cvvErrorMessage && (
                            <p className="text-xs font-bold text-red-500 mt-1">
                              {cvvErrorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    </form>
                    <p className="text-sm font-normal leading-[160%] text-[#485868] mb-[30px]">
                      By making a purchase on our website, you are agreeing to
                      the{" "}
                      <Link className="text-primary">Terms of Service.</Link>{" "}
                      Your enrollment is protected by our Lead Assurance Money
                      Back Guarantee.
                    </p>
                    <div>
                      <IconButton
                        title="add new payment method"
                        classes="rounded-[10px] bg-primary text-sm leading-[160%] md:text-base font-black uppercase text-white py-2 md:p-4 px-3 flex justify-center items-center gap-[6px] w-full mb-4"
                        icon={PlusCircleIcon}
                        iconClasses="w-6 h-6 text-white"
                        onClick={(e) => {
                          formik.handleSubmit();

                          if (formik.isValid) {
                            handleStripePayment(e);
                          }
                        }}
                        buttonLoader={paymentLoader}
                      />
                      <button
                        type="button"
                        className="rounded-[10px] bg-[#EBEFF3] text-sm leading-[160%] md:text-base font-black uppercase text-[#485868] py-2 md:p-4 px-3 flex justify-center items-center gap-[6px] w-full"
                        onClick={() => handleClose()}
                      >
                        CANCEL
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
export default NewPaymentForm;
